import React, { useEffect, useState, useCallback } from "react";
import {
  Paper, Typography, IconButton, Button, CircularProgress, Alert, Box, Tooltip
} from "@mui/material";
import { Add, Refresh } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import {
  getAnonymizationProfiles,
  createAnonymizationProfile,
  updateAnonymizationProfile,
  deleteAnonymizationProfile
} from "../../services/anonymizationProfilesService";

import PseudoDicomProfileCard from "./PseudoDicomProfileCard"; // The card we built previously
import DicomProfileEditDialog from "./DicomProfileEditDialog";

const PROCESS_TYPE = "dicom_pseudonymization";

const PseudoDicomProfilesPanel = () => {
  const { t } = useTranslation();

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);

  const loadProfiles = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getAnonymizationProfiles(PROCESS_TYPE);
      setProfiles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(t("DicomProfilesPanel.loadError", "Nepodarilo sa načítať DICOM profily."));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const handleCreateClick = () => {
    setEditingProfile(null);
    setDialogOpen(true);
  };

  const handleEditClick = (profile) => {
    setEditingProfile(profile);
    setDialogOpen(true);
  };

  const handleDeleteClick = async (profile) => {
    const confirmed = window.confirm(
      t("DicomProfilesPanel.deleteConfirm", 'Naozaj chcete odstrániť DICOM profil "{{name}}"?', { name: profile.name })
    );
    if (!confirmed) return;

    try {
      setError("");
      await deleteAnonymizationProfile(profile.id);
      await loadProfiles();
    } catch (err) {
      console.error(err);
      setError(t("DicomProfilesPanel.deleteError", "Nepodarilo sa odstrániť profil."));
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingProfile(null);
  };

  const handleDialogSave = async (payload) => {
    try {
      setError("");
      if (editingProfile) {
        await updateAnonymizationProfile(editingProfile.id, payload);
      } else {
        await createAnonymizationProfile(payload, PROCESS_TYPE);
      }
      setDialogOpen(false);
      setEditingProfile(null);
      await loadProfiles();
    } catch (err) {
      console.error(err);
      setError(t("DicomProfilesPanel.saveError", "Nepodarilo sa uložiť profil."));
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        borderRadius: 3, 
        border: '1px solid', 
        borderColor: 'divider', 
        bgcolor: 'background.paper' 
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("DicomProfilesPanel.title", "Vaše DICOM Profily")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={t("common.refresh", "Obnoviť")}>
            <span>
              <IconButton size="small" onClick={loadProfiles} disabled={loading} sx={{ bgcolor: 'action.hover' }}>
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Button 
            variant="contained" 
            size="small" 
            startIcon={<Add />} 
            onClick={handleCreateClick}
            disableElevation
            sx={{ borderRadius: 2, fontWeight: 600, px: 2 }}
          >
            {t("DicomProfilesPanel.createButton", "Nový DICOM profil")}
          </Button>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {error && !loading && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
        </Box>
      )}

      {!loading && profiles.length === 0 && !error && (
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 4, 
            textAlign: 'center', 
            bgcolor: 'action.hover', 
            borderRadius: 2, 
            borderStyle: 'dashed' 
          }}
        >
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            {t("DicomProfilesPanel.empty", "Zatiaľ nemáte vytvorený žiadny DICOM profil.")}
          </Typography>
        </Paper>
      )}

      {!loading && profiles.length > 0 && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: 2 }}>
          {profiles.map((profile) => (
            <PseudoDicomProfileCard
              key={profile.id}
              profile={profile}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
            />
          ))}
        </Box>
      )}

      <DicomProfileEditDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        initialData={editingProfile}
      />
    </Paper>
  );
};

export default PseudoDicomProfilesPanel;